import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AutoRechargeCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [creatingTpl, setCreatingTpl] = useState(false);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/auto-recharge/config`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (res.ok) setCfg(await res.json());
      } catch (e) { /* noop */ }
    })();
  }, []);

  const patch = (updates) => setCfg((c) => ({ ...c, ...updates }));

  const save = async (overrides = {}) => {
    setSaving(true);
    const payload = { ...cfg, ...overrides };
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auto-recharge/config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: !!payload.enabled,
          points_threshold: parseInt(payload.points_threshold) || 0,
          contingent_threshold: parseInt(payload.contingent_threshold) || 0,
          cooldown_days: parseInt(payload.cooldown_days) || 0,
          email_enabled: payload.email_enabled !== false,
          whatsapp_enabled: payload.whatsapp_enabled !== false,
          include_subscriptions: payload.include_subscriptions !== false,
        }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setCfg(d.config);
      toast.success('Auto-Aufladung gespeichert.');
    } catch (e) {
      toast.error('Konnte nicht speichern.');
    } finally {
      setSaving(false);
    }
  };

  const createTemplate = async () => {
    setCreatingTpl(true);
    const alreadyExists = !!cfg.whatsapp_template_name;
    try {
      // Existiert die Vorlage schon → nur Freigabe-Status von Meta holen; sonst anlegen.
      const url = alreadyExists
        ? `${BACKEND_URL}/api/admin/auto-recharge/sync-template-langs`
        : `${BACKEND_URL}/api/whatsapp/templates/create-auto-recharge`;
      const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Fehler');
      const approved = d.approved || d.approved_languages || [];
      const statusMap = d.status_by_lang || {};
      const total = Object.keys(statusMap).length || approved.length;
      setCfg((c) => ({
        ...c,
        whatsapp_template_name: d.name || c.whatsapp_template_name || 'auto_aufladung_erinnerung',
        whatsapp_template_langs: approved,
        whatsapp_template_status_by_lang: statusMap,
      }));
      toast.success(`Meta-Vorlage: ${approved.length}/${total || '?'} Sprachen freigegeben (APPROVED).`);
    } catch (e) {
      toast.error(`Fehlgeschlagen: ${e.message || 'unbekannt'}`);
    } finally {
      setCreatingTpl(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auto-recharge/run-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error();
      toast.success(`Scan ausgeführt: ${d.reminded ?? 0} Erinnerungen, ${d.skipped ?? 0} übersprungen.`);
    } catch (e) {
      toast.error('Scan fehlgeschlagen.');
    } finally {
      setRunning(false);
    }
  };

  if (!cfg) return null;

  return (
    <Card className="border-yellow-300" data-testid="auto-recharge-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Zap size={18} className="text-yellow-500" />
            Auto-Aufladung bei leeren Punkten/Abo
          </span>
          <Switch
            data-testid="auto-recharge-enabled-switch"
            checked={!!cfg.enabled}
            onCheckedChange={(v) => { patch({ enabled: v }); save({ enabled: v }); }}
          />
        </CardTitle>
        <CardDescription>
          Kunden mit niedrigem Guthaben werden gefragt, ob ihr zuletzt gekauftes Paket automatisch
          nachgeladen werden soll. Bei „Ja" (WhatsApp) oder Klick (E-Mail) wird sofort gutgeschrieben
          und eine offene Rechnung erstellt – inkl. Kundenrabatt.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className={`space-y-4 ${cfg.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="ar-points-threshold">Erinnern ab Punkten ≤</Label>
              <Input
                id="ar-points-threshold"
                data-testid="auto-recharge-points-threshold"
                type="number"
                value={cfg.points_threshold ?? 6}
                onChange={(e) => patch({ points_threshold: e.target.value })}
                onBlur={() => save()}
              />
            </div>
            <div>
              <Label htmlFor="ar-contingent-threshold">Erinnern ab Abo-Guthaben ≤</Label>
              <Input
                id="ar-contingent-threshold"
                data-testid="auto-recharge-contingent-threshold"
                type="number"
                value={cfg.contingent_threshold ?? 6}
                onChange={(e) => patch({ contingent_threshold: e.target.value })}
                onBlur={() => save()}
              />
            </div>
            <div>
              <Label htmlFor="ar-cooldown">Erneut frühestens nach (Tage)</Label>
              <Input
                id="ar-cooldown"
                data-testid="auto-recharge-cooldown"
                type="number"
                value={cfg.cooldown_days ?? 7}
                onChange={(e) => patch({ cooldown_days: e.target.value })}
                onBlur={() => save()}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex items-center justify-between p-3 border rounded-lg">
              <span className="text-sm">📧 E-Mail-Erinnerung</span>
              <Switch
                data-testid="auto-recharge-email-switch"
                checked={cfg.email_enabled !== false}
                onCheckedChange={(v) => { patch({ email_enabled: v }); save({ email_enabled: v }); }}
              />
            </label>
            <label className="flex items-center justify-between p-3 border rounded-lg">
              <span className="text-sm">📱 WhatsApp-Erinnerung</span>
              <Switch
                data-testid="auto-recharge-whatsapp-switch"
                checked={cfg.whatsapp_enabled !== false}
                onCheckedChange={(v) => { patch({ whatsapp_enabled: v }); save({ whatsapp_enabled: v }); }}
              />
            </label>
            <label className="flex items-center justify-between p-3 border rounded-lg">
              <span className="text-sm">Auch Abos einbeziehen</span>
              <Switch
                data-testid="auto-recharge-subscriptions-switch"
                checked={cfg.include_subscriptions !== false}
                onCheckedChange={(v) => { patch({ include_subscriptions: v }); save({ include_subscriptions: v }); }}
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={() => save()} disabled={saving} data-testid="auto-recharge-save-btn">
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Speichern
            </Button>
            <Button variant="outline" onClick={runNow} disabled={running} data-testid="auto-recharge-run-now-btn">
              {running ? <Loader2 size={16} className="animate-spin mr-2" /> : <RefreshCw size={16} className="mr-2" />}
              Jetzt scannen
            </Button>
          </div>
        </div>

        <div className="mt-3 p-3 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <span className="font-medium">WhatsApp-Meta-Vorlage:</span>{' '}
                {cfg.whatsapp_template_name ? (
                  <span data-testid="auto-recharge-template-status">
                    <code>{cfg.whatsapp_template_name}</code>
                    {' · '}
                    <span className="text-green-600">
                      {(cfg.whatsapp_template_langs || []).length} Sprache(n) freigegeben
                    </span>
                    {cfg.whatsapp_template_status_by_lang && (
                      <span className="text-gray-500">
                        {' '}({Object.entries(cfg.whatsapp_template_status_by_lang)
                          .map(([l, s]) => `${l}:${s === 'APPROVED' ? '✓' : s}`)
                          .join(', ')})
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-gray-500">noch nicht angelegt</span>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Proaktive WhatsApp-Erinnerungen nutzen nur von Meta <strong>freigegebene</strong>
                  {' '}Sprachen automatisch – noch nicht freigegebene fallen auf Deutsch zurück. Der
                  {' '}Status wird täglich automatisch aktualisiert. Die „Ja"-Bestätigung läuft als Freitext.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={createTemplate}
                disabled={creatingTpl}
                data-testid="auto-recharge-create-template-btn"
              >
                {creatingTpl ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                {cfg.whatsapp_template_name ? 'Vorlage-Status prüfen' : 'Vorlage bei Meta anlegen'}
              </Button>
            </div>
          </div>
      </CardContent>
    </Card>
  );
};

export default AutoRechargeCard;
